import React, { useState } from 'react';
import { AutoComplete, Input, Typography, theme } from 'antd';
import { LoadingOutlined, SearchOutlined } from '@ant-design/icons';
import { usePlaceSearch } from '../viewmodels/usePlaceSearch';

// Server-backed place search (replaces the Google Places Autocomplete widget).
// Works controlled (value/onChange, e.g. inside Form.Item) or uncontrolled.
// onSelect receives the server's place shape: {place_id, name, formatted_address,
// latitude, longitude, country_code, region, region_short}.
// onEnter(text) may handle Enter/the button itself (coordinates, links) by
// returning true; otherwise Enter takes the first match for the typed text.
// isLocationText(text) marks text (links, coordinates) that should not be
// searched as a place; pasted text it accepts replaces the field and goes to
// onPasteText(text) at once. hint shows under the field.
export default function PlaceSearchInput({ value, onChange, onSelect, onEnter, onPasteText, isLocationText, hint, enterButton, loading, size, placeholder = 'Search for a location', disabled, style, ariaLabel, fillOnSelect = true }) {
  const { token } = theme.useToken();
  const [ownText, setOwnText] = useState('');
  const controlled = value !== undefined;
  const text = controlled ? (value || '') : ownText;
  const setText = next => { if (!controlled) setOwnText(next); onChange?.(next); };
  const places = usePlaceSearch({
    onSelect: place => {
      if (fillOnSelect) setText(place.formatted_address || place.name || '');
      onSelect?.(place);
    },
  });
  const enter = typed => { if (onEnter?.(typed)) { places.clear(); return; } places.selectFirst(typed); };
  const options = places.options.map(row => ({
    value: row.place_id,
    label: <div style={{ lineHeight: 1.3, padding: '2px 0' }}>
      <div>{row.primary_text}</div>
      {row.secondary_text && <Typography.Text type="secondary" style={{ fontSize: 12 }}>{row.secondary_text}</Typography.Text>}
    </div>,
  }));
  const inputProps = {
    'aria-label': ariaLabel || placeholder, placeholder, size, prefix: <SearchOutlined style={{ color: token.colorTextTertiary }} />,
    onPaste: onPasteText && isLocationText ? event => {
      const pasted = event.clipboardData?.getData('text') || '';
      if (!pasted || !isLocationText(pasted)) return;
      // A pasted link or coordinates replaces the field (no second input event
      // to race the pin), then the handler sets the pin from it.
      event.preventDefault();
      places.clear();
      setText(pasted.trim());
      onPasteText(pasted.trim());
    } : undefined,
  };
  return <div style={style}>
    <AutoComplete
      value={text}
      options={options}
      disabled={disabled}
      style={{ width: '100%' }}
      popupMatchSelectWidth
      onChange={next => {
        if (places.options.some(row => row.place_id === next)) return;
        setText(next);
        if (isLocationText?.(next)) places.clear(); else places.search(next);
      }}
      onSelect={placeId => { places.select(placeId); }}
      notFoundContent={places.loading ? 'Searching…' : null}
    >
      {enterButton
        // With suggestions open, Enter belongs to the dropdown; the button always searches.
        ? <Input.Search {...inputProps} enterButton={enterButton} loading={loading || places.loading} onSearch={(typed, event) => { if (event?.type === 'keydown' && places.options.length) return; enter(typed); }} />
        // The suffix is always present so the spinner doesn't remount the input.
        : <Input {...inputProps} suffix={loading ? <LoadingOutlined aria-label="Reading location" /> : <span />} onKeyDown={event => { if (event.key === 'Enter' && !places.options.length) { event.preventDefault(); enter(text); } }} />}
    </AutoComplete>
    {hint && !places.error && <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 4 }}>{hint}</Typography.Text>}
    {places.error && <Typography.Text type="warning" style={{ display: 'block', fontSize: 12, marginTop: 4 }}>{places.error}</Typography.Text>}
  </div>;
}
